import { type ExprNode, add, mul, pow, func } from './node';

// ---------------------------------------------------------------------------
// traverse — pre-order visitor
// ---------------------------------------------------------------------------

/**
 * Pre-order traversal: calls `fn(node)` first, then recurses into children.
 * num/var have no children — only `fn` is called on them.
 */
export function traverse(node: ExprNode, fn: (n: ExprNode) => void): void {
  fn(node);

  switch (node.kind) {
    case 'num':
    case 'var': {
      // No children.
      break;
    }
    case 'add':
    case 'mul': {
      for (const child of node.children) {
        traverse(child, fn);
      }
      break;
    }
    case 'pow': {
      traverse(node.base, fn);
      traverse(node.exp, fn);
      break;
    }
    case 'func': {
      for (const arg of node.args) {
        traverse(arg, fn);
      }
      break;
    }
  }
}

// ---------------------------------------------------------------------------
// transform — bottom-up mapper
// ---------------------------------------------------------------------------

/**
 * Bottom-up transformation: first rebuilds every child by recursively
 * applying `transform`, then calls `fn(rebuiltNode)` and returns its result.
 * `fn`'s returned replacement is NOT recursed into (no infinite loops).
 * num/var: just returns `fn(node)` directly.
 */
export function transform(node: ExprNode, fn: (n: ExprNode) => ExprNode): ExprNode {
  switch (node.kind) {
    case 'num':
    case 'var': {
      return fn(node);
    }
    case 'add': {
      const newChildren = node.children.map((child) => transform(child, fn));
      const rebuilt: ExprNode = add(newChildren);
      return fn(rebuilt);
    }
    case 'mul': {
      const newChildren = node.children.map((child) => transform(child, fn));
      const rebuilt: ExprNode = mul(newChildren);
      return fn(rebuilt);
    }
    case 'pow': {
      const newBase = transform(node.base, fn);
      const newExp = transform(node.exp, fn);
      const rebuilt: ExprNode = pow(newBase, newExp);
      return fn(rebuilt);
    }
    case 'func': {
      const newArgs = node.args.map((arg) => transform(arg, fn));
      const rebuilt: ExprNode = func(node.name, newArgs);
      return fn(rebuilt);
    }
  }
}
